import { createRef, useEffect } from "react";
import type { SyntheticEvent } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

import { Adg4RaisedMedia } from "@/components/adg4/raised-media/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import {
    FeatureInfo,
    FeatureInfoProps
} from "@/patterns/feature-info/feature-info";

const imageStyle = css({
    paddingBlockEnd: 24,
    [media.above.md]: {
        paddingBlockStart: 24,
        paddingInlineStart: 24
    }
});

const backgroundStyle = css({
    "&:before": {
        "content": '""',
        "backgroundColor": "#82B536",
        "position": "absolute",
        "left": "-1%",
        "top": "0",
        "width": "102%",
        "height": "100%",
        "zIndex": -1,
        "clipPath":
            "polygon(100% 0%, 100% 33%, 55% 33%, 55% 100%, 3% 97%, 2% 33%, 0% 33%, 0% 1%)",
        "@media (max-width: 1175px)": {
            height: "95%",
            insetBlockStart: "2.5%"
        },
        "@media (max-width: 1150px)": {
            height: "92.5%",
            insetBlockStart: "3.75%"
        },
        "@media (max-width: 1125px)": {
            height: "90%",
            insetBlockStart: "5%"
        },
        "@media (max-width: 1100px)": {
            height: "85%",
            insetBlockStart: "7.5%"
        },
        "@media (max-width: 1075px)": {
            height: "80%",
            insetBlockStart: "10%"
        },
        "@media (max-width: 1050px)": {
            height: "75%",
            insetBlockStart: "12.5%"
        },
        [media.below.md]: {
            content: "none"
        }
    },
    "&:after": {
        "content": '""',
        "backgroundImage":
            "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/scroll-teams-accent-4.webp)",
        "position": "absolute",
        "left": "-112px",
        "bottom": "-145px",
        "width": "274px",
        "height": "337px",
        "scale": "30%",
        "@media (max-width: 1175px)": {
            scale: "25%",
            insetBlockEnd: "-135px"
        },
        "@media (max-width: 1150px)": {
            insetBlockEnd: "-130px"
        },
        "@media (max-width: 1125px)": {
            insetBlockEnd: "-125px"
        },
        "@media (max-width: 1100px)": {
            insetBlockEnd: "-120px"
        },
        "@media (max-width: 1075px)": {
            scale: "20%",
            insetBlockEnd: "-105px"
        },
        "@media (max-width: 1050px)": {
            insetBlockEnd: "-100px"
        },
        [media.below.md]: {
            content: "none"
        }
    }
});

const videoSizing = css({
    display: "flex",
    [media.below.sm]: {
        height: "52vw"
    },
    [media.only.sm]: {
        height: "40vw"
    }
});

const videoStyle = css({
    maxWidth: "100%"
});

export const ContentTypeVideo = ({
    active,
    videoSrcMp4,
    videoSrcWebm,
    videoPreviewSrc,
    showBackground,
    ...props
}: FeatureInfoProps & {
    active?: boolean;
    videoSrcMp4: string;
    videoSrcWebm: string;
    videoPreviewSrc: string;
    showBackground?: boolean;
}) => {
    const { userSelected, goToNextTab } = useAdg4TabsContext();
    const { ref: containerRef, inView: sectionIsVisible } = useInView({
        initialInView: true,
        threshold: 0.3
    });
    const videoRef = createRef<HTMLVideoElement>();

    useEffect(() => {
        if (
            sectionIsVisible &&
            active &&
            videoRef.current &&
            videoRef.current.play
        ) {
            videoRef.current.autoplay = true;
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {
                // Ignore error likely due to autoplay restrictions
            });
        } else if (!active || !sectionIsVisible) {
            if (videoRef.current?.paused) {
                return;
            }

            videoRef.current?.pause();
        }
    }, [active, videoRef, sectionIsVisible]);

    return (
        <FeatureInfo {...props} reverseLayout>
            <div
                ref={containerRef}
                css={[imageStyle, showBackground && backgroundStyle]}
            >
                <Adg4RaisedMedia>
                    <div css={videoSizing}>
                        <video
                            css={videoStyle}
                            playsInline={true}
                            muted={true}
                            preload="auto"
                            poster={videoPreviewSrc}
                            autoPlay={!!active}
                            loop={userSelected}
                            ref={videoRef}
                            onEnded={() => {
                                if (!userSelected) {
                                    goToNextTab();
                                }
                            }}
                            // Use onLoadedMetadata to trigger a Safari autplay workaround
                            onLoadedMetadata={(
                                videoEvent: SyntheticEvent<
                                    HTMLVideoElement,
                                    Event
                                >
                            ) => {
                                if (!active) {
                                    return;
                                }
                                try {
                                    videoEvent.currentTarget.currentTime = 0; // Set currentTime to workaround Safari not autplaying
                                    if (
                                        videoEvent.currentTarget.parentElement
                                    ) {
                                        videoEvent.currentTarget
                                            .play()
                                            .catch(() => false); // Call play() as backup to ensure the video is playing
                                    }
                                } catch (error) {
                                    console.log(
                                        "Error calling play method on video after onCanPlay is invoked."
                                    );
                                }
                            }}
                        >
                            <source src={videoSrcMp4} type={`video/mp4`} />
                            <source src={videoSrcWebm} type={`video/webm`} />
                        </video>
                    </div>
                </Adg4RaisedMedia>
            </div>
        </FeatureInfo>
    );
};
